import axios, { InternalAxiosRequestConfig } from 'axios';
import { isNativeApp } from '../../utils/pwa';
import { detectPlatform } from '../../utils/platform';
import { isOnboardingExemptPath } from '../../utils/onboardingExemptPaths';
import { TOS_REQUIRED_EVENT } from '../../utils/tosEvents';
import { ORG_PICKER_REQUIRED_EVENT, OrgPickerRequiredEventDetail } from '../../utils/orgPickerEvents';

// Capacitor native platform detection (used for concurrency limits)
const isNativePlatform = isNativeApp();

// Determine API URL based on environment:
// - Explicit REACT_APP_API_URL set at build time (e.g. production deploys)
// - Default: relative /api (works for both browser/PWA via nginx proxy
//   and Capacitor native apps in remote URL mode via same-origin)
function resolveApiUrl(): string {
  if (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL !== '/api') {
    return process.env.REACT_APP_API_URL;
  }
  return '/api';
}

export const API_URL = resolveApiUrl();

// Custom event for session expiry - allows AuthContext to listen and handle gracefully
export const AUTH_ERROR_EVENT = 'auth:session-expired';

// Custom event for successful login - allows OrganizationContext to fetch orgs
export const AUTH_LOGIN_EVENT = 'auth:login-success';

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// 0zzz-fe: while OrgPickerSheet is open and awaiting the user's re-selection,
// block outgoing writes client-side too (defence in depth — see the request
// interceptor below for why, and orgPickerEvents.ts for the event contract).
// Exported setter; OrgPickerSheet sets this true the instant it claims the
// ORG_PICKER_REQUIRED_EVENT and false only once the user has resolved it
// (selectOrganization succeeded, or they left via "Go to Organizations").
let orgSelectionPending = false;
export function setOrgSelectionPending(pending: boolean): void {
  orgSelectionPending = pending;
}

// Orgs the user moved away from in the picker. A later org refusal that names
// one of them (header or URL) is a leftover from the old selection and must not
// clear the org they just picked. Per-tab, cleared on reload. Only id-shaped
// values (UUID public ids, >= 8 chars) are kept: a short legacy id such as '1'
// would substring-match unrelated URLs and silently swallow real refusals.
const abandonedOrgIds = new Set<string>();
const ABANDONED_ORG_IDS_MAX = 20;

// Just after a pick, every still-mounted query refetches under the new org. A
// query for a resource that lives in the org just left (e.g. GET /tasks/<id>)
// is refused WITHOUT naming that org in its URL, so the id-based guard can't see
// it. For a short window, org refusals on READS are therefore treated as
// leftovers: rejected with no clear or redirect (cross-provider review, 6th pass).
const POST_PICK_READ_GRACE_MS = 15_000;
let lastOrgPickAt = 0;
// Measured from when the request was CREATED, not when its response lands: the
// post-pick refetch storm queues behind MAX_CONCURRENT and can finish well after
// 15s (7th cross-provider pass). Requests created before the pick carry the old
// org's header and are caught by the sent-vs-stored check instead.
const REQUEST_CREATED_AT = '__helmCreatedAt';
function withinPostPickGrace(config: Record<string, unknown> | undefined): boolean {
  if (lastOrgPickAt <= 0) return false;
  const created = typeof config?.[REQUEST_CREATED_AT] === 'number' ? (config[REQUEST_CREATED_AT] as number) : Date.now();
  return created >= lastOrgPickAt && created - lastOrgPickAt < POST_PICK_READ_GRACE_MS;
}

export function markOrgAbandoned(orgId: string | undefined): void {
  const id = orgId ? String(orgId) : '';
  if (id.length < 8) return;
  if (abandonedOrgIds.size >= ABANDONED_ORG_IDS_MAX) {
    const oldest = abandonedOrgIds.values().next().value; // Set keeps insertion order
    if (oldest !== undefined) abandonedOrgIds.delete(oldest);
  }
  abandonedOrgIds.add(id);
  lastOrgPickAt = Date.now();
}

// The org a request names in its BODY (`organizationId`). The client only ever
// sends its own selected org there, so a membership refusal for it is about the
// caller. The URL is deliberately NOT used: routes under /organizations/:id also
// check OTHER users' membership (e.g. keyholder assignment for a member who just
// left), and that refusal must not be read as "your org is stale".
function orgIdInRequestBody(config: { data?: unknown } | undefined): string | undefined {
  if (!config) return undefined;
  let body: unknown = config.data;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      body = undefined;
    }
  }
  const fromBody = (body as { organizationId?: unknown } | null)?.organizationId;
  return typeof fromBody === 'string' || typeof fromBody === 'number' ? String(fromBody) : undefined;
}

/** Forget orgs abandoned in the picker — on session change they belong to someone else. */
export function resetAbandonedOrgIds(): void {
  abandonedOrgIds.clear();
  lastOrgPickAt = 0;
}

// A SUBSET of backend/src/services/tosEnforcement.ts's exemptions: its
// TOS_EXEMPT_PREFIXES safety families, plus the consent-exit actions from its
// TOS_EXEMPT_ROUTES (leave, block, decline, terminate, suspend, withdraw) as
// path fragments. Nothing safety- or consent-critical may be blockable by an
// org-selection prompt. Backend entries there are absolute (/api/...); axios
// configs here carry the URL relative to API_URL's baseURL (already '/api'),
// so each fragment below is the backend one with its leading '/api' stripped.
// `.includes` rather than `startsWith` is deliberate — over-exempting a
// route from this LOCAL, best-effort lock is harmless (the server still
// enforces ORG_HEADER_INVALID/ORG_SELECTION_REQUIRED itself), but
// under-exempting a safety route is not.
const ORG_SELECTION_EXEMPT_URL_FRAGMENTS = [
  '/auth/', // includes /auth/logout — must always be reachable
  '/safeword',
  '/safety-hub',
  '/safety',
  '/checkins',
  '/checkin-responses',
  '/aftercare',
  '/limits',
  '/reports',
  '/chastity',
  '/drop-detection',
  '/discovery/safety',
  // consent exits (TOS_EXEMPT_ROUTES)
  '/leave',
  '/block',
  '/decline',
  '/terminate',
  '/suspend',
  '/withdraw',
  '/reject', // contract amendment reject
  '/help-requests', // incl. consent_withdrawal requests and their cancel
  '/report', // incl. /messages/messages/report ('/reports' above is a subset)
  '/relationship-requests',
  '/end', // POST /organizations/:id/relationships/:rid/end
];

function isOrgSelectionExempt(url: string | undefined): boolean {
  if (!url) return false;
  return ORG_SELECTION_EXEMPT_URL_FRAGMENTS.some((fragment) => url.includes(fragment));
}

// CSRF token storage (in-memory for security - not persisted)
let csrfToken: string | null = null;
let csrfTokenFetchPromise: Promise<string | null> | null = null;

// Singleton promise for token refresh — prevents race condition where multiple
// 401 responses each try to consume the one-time-use refresh token
let refreshTokenPromise: Promise<{ token: string; refreshToken: string } | null> | null = null;

export const dispatchAuthError = (message: string) => {
  window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT, { detail: { message } }));
};

// ---------------------------------------------------------------------------
// Spec 284: proactive/silent refresh
//
// The reactive 401-triggered refresh above only fires once the access token
// has ALREADY expired. Since the refresh token used to die at essentially the
// same moment (both were 7d), a daily user's activity never actually renewed
// anything — the first refresh attempt of the week was also the last, right as
// the refresh token itself expired. This proactive check renews both tokens
// (mintTokenPair()/persistRefreshToken() always issue a full fresh TTL) once
// the access token's remaining lifetime drops below ~50%, so an actively-used
// app never lets the refresh token approach its own expiry. A genuinely idle
// user (no check fires within the TTL window) still lets both tokens die
// naturally — that idle-timeout behavior is intentional, not a bug.
//
// Must match the backend's ACCESS_TOKEN_EXPIRY ('7d' — unchanged by Spec 284,
// see the spec's Open Questions). Not derived from the refresh-token TTL,
// which is a separate, now-30-day constant.
const ACCESS_TOKEN_EXPIRY_SECONDS = 7 * 24 * 60 * 60;
const PROACTIVE_REFRESH_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// Decode a JWT's payload without verifying it — verification happens server-side;
// this is purely a local hint for "is it worth trying to refresh yet."
function decodeJwtExp(token: string): number | null {
  try {
    const payloadSegment = token.split('.')[1];
    if (!payloadSegment) return null;
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

async function performProactiveRefreshIfNeeded(): Promise<void> {
  const storedToken = localStorage.getItem('authToken');
  const storedRefresh = localStorage.getItem('refreshToken');
  // No refresh token stored — impersonation and DOB-self-registration sessions
  // are deliberately out of Spec 284's scope and never get one (see spec's
  // Scope table); nothing to proactively renew for them.
  if (!storedToken || !storedRefresh) return;

  const exp = decodeJwtExp(storedToken);
  if (exp === null) return;

  const remainingSeconds = exp - Date.now() / 1000;
  if (remainingSeconds > ACCESS_TOKEN_EXPIRY_SECONDS / 2) return; // not yet past the halfway point

  // Reuse the same singleton as the reactive 401 path — refresh tokens are
  // one-time-use, so a proactive check racing a reactive 401 must share one
  // in-flight refresh call, not consume two tokens for one renewal.
  if (!refreshTokenPromise) {
    refreshTokenPromise = axios.post(`${API_URL}/auth/refresh`, { refreshToken: storedRefresh })
      .then((resp) => {
        const { token: newAccess, refreshToken: newRefresh } = resp.data;
        localStorage.setItem('authToken', newAccess);
        localStorage.setItem('refreshToken', newRefresh);
        return { token: newAccess, refreshToken: newRefresh };
      })
      .catch((err) => {
        // Fail closed: leave the existing (still-valid-for-now) tokens in place
        // and let the reactive 401 path handle it when the access token actually
        // expires, rather than tearing down session state on a transient error.
        if (err?.code === 'ERR_NETWORK' || err?.code === 'ECONNABORTED') {
          console.warn('[auth] Proactive refresh failed — network error, not invalidating session');
        }
        return null;
      })
      .finally(() => { refreshTokenPromise = null; });
  }

  await refreshTokenPromise;
}

// Exposed for AppLockService.ts's appStateChange 'isActive' branch (mobile
// foreground/resume — see that file for why it's wired there and not inside
// onForeground()). Fire-and-forget: internal errors are already handled
// fail-closed above, nothing for a caller to await or catch.
export function triggerProactiveRefreshCheck(): void {
  performProactiveRefreshIfNeeded().catch(() => {
    // performProactiveRefreshIfNeeded already fails closed internally; this
    // catch only guards against an unexpected synchronous throw.
  });
}

// An immediate check on module load (covers a cold start where the stored
// token is already past the halfway point — e.g. the tab was closed, not just
// backgrounded, so no 'visibilitychange' ever fires for this load), plus a
// periodic check while the app/tab stays open, plus an immediate check
// whenever the tab/PWA regains visibility (covers "was backgrounded for
// days" — mobile's native foreground signal is wired separately via
// AppLockService, which is unaffected by 'visibilitychange' reliability in a
// Capacitor WebView). Skipped under Jest (react-scripts test) — this is
// app-runtime behavior, not something a unit test importing this module
// should trigger.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
  performProactiveRefreshIfNeeded().catch(() => {});

  setInterval(() => { performProactiveRefreshIfNeeded().catch(() => {}); }, PROACTIVE_REFRESH_CHECK_INTERVAL_MS);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      performProactiveRefreshIfNeeded().catch(() => {});
    }
  });
}

// Read version from package.json at build time via CRA env injection
const APP_VERSION = process.env.REACT_APP_VERSION || '2026.10.17.1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-App-Version': APP_VERSION,
  },
});

// ---------------------------------------------------------------------------
// Request concurrency limiter
//
// Caps concurrent in-flight requests to avoid overwhelming the server DB pool.
// Native apps get higher concurrency — dedicated device with full networking stack.
// Browser/PWA stays conservative for shared browser tab context.
// ---------------------------------------------------------------------------
const MAX_CONCURRENT = isNativePlatform ? 6 : 4;
const MAX_QUEUE_SIZE = 50;
const QUEUE_TIMEOUT_MS = 30_000;
let activeRequests = 0;
const requestQueue: Array<{ resolve: () => void }> = [];

function acquireSlot(): Promise<void> {
  if (activeRequests < MAX_CONCURRENT) {
    activeRequests++;
    return Promise.resolve();
  }
  // The gate's own rejections never took a slot: mark them noSlotHeld so the
  // response-side release doesn't give one back (DeepSeek review, 0zzz-fe) —
  // reachable exactly when the system is saturated.
  if (requestQueue.length >= MAX_QUEUE_SIZE) {
    return Promise.reject(Object.assign(new Error('Too many pending requests'), { noSlotHeld: true }));
  }
  return new Promise<void>((resolve, reject) => {
    const entry = { resolve: () => { clearTimeout(timeout); resolve(); } };
    // A timed-out waiter must leave the queue, or the next freed slot is
    // handed to it and lost for good.
    const timeout = setTimeout(() => {
      const i = requestQueue.indexOf(entry);
      if (i !== -1) requestQueue.splice(i, 1);
      reject(Object.assign(new Error('Request queue timeout'), { noSlotHeld: true }));
    }, QUEUE_TIMEOUT_MS);
    requestQueue.push(entry);
  });
}

function releaseSlot(): void {
  if (requestQueue.length > 0) {
    const next = requestQueue.shift()!;
    next.resolve();
  } else {
    activeRequests--;
  }
}

/**
 * Fetch a fresh CSRF token from the server
 * Uses a singleton promise to prevent duplicate requests
 */
async function fetchCSRFToken(): Promise<string | null> {
  // If there's already a fetch in progress, wait for it
  if (csrfTokenFetchPromise) {
    return csrfTokenFetchPromise;
  }

  csrfTokenFetchPromise = (async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return null;
      }

      const response = await axios.get(`${API_URL}/auth/csrf-token`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      csrfToken = response.data.data.csrfToken;
      return csrfToken;
    } catch {
      return null;
    } finally {
      csrfTokenFetchPromise = null;
    }
  })();

  return csrfTokenFetchPromise;
}

/**
 * Get current CSRF token, fetching if needed
 */
async function getCSRFToken(): Promise<string | null> {
  const authToken = localStorage.getItem('authToken');
  if (!authToken) {
    return null;
  }

  if (!csrfToken) {
    // No try/catch: fetchCSRFToken() has its own internal try/catch/finally and
    // always resolves, even on network failure — it never rejects. The catch
    // that used to wrap this call could therefore never execute (dead code,
    // found while raising this file's branch coverage 2026-09-07). On failure
    // csrfToken simply stays null and we return null below, which is what the
    // unreachable handler did anyway.
    await fetchCSRFToken();
  }

  return csrfToken;
}

/**
 * Clear CSRF token (call on logout)
 */
export function clearCSRFToken(): void {
  csrfToken = null;
  csrfTokenFetchPromise = null;
}

// Force-refresh and return the new token. Used by offline queue replay where
// stored headers may have a token older than the 30-min server TTL.
export async function getFreshCSRFToken(): Promise<string | null> {
  csrfToken = null;
  await fetchCSRFToken();
  return csrfToken;
}

// Errors created by the org-selection write lock carry `noSlotHeld: true`:
// axios 1.x runs request interceptors last-registered-first, so the lock
// (registered last) rejects BEFORE the gate below has acquired a slot, and
// releasing one for it would lift the MAX_CONCURRENT cap. Every other error
// still releases, exactly as before (some carry no config, so per-request
// tracking can't tell them apart — see 0zzz-fe review).
function releaseSlotUnlessNoneHeld(error: unknown): void {
  if (!(error as { noSlotHeld?: boolean } | null)?.noSlotHeld) releaseSlot();
}

// Concurrency gate — wait for a slot before sending
api.interceptors.request.use(
  async (config) => { await acquireSlot(); return config; },
  (error) => { releaseSlotUnlessNoneHeld(error); return Promise.reject(error); }
);

// Add auth token to requests and handle FormData properly.
// This runs BEFORE the concurrency gate (axios runs request interceptors
// last-registered-first), so a failure here holds no slot: mark it noSlotHeld
// or both release handlers give one back (e.g. storage access throwing when
// cookies/storage are blocked — cross-provider review, 0zzz-fe).
async function attachRequestHeaders(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  const token = localStorage.getItem('authToken');
  // Don't clobber a caller-supplied Authorization header (e.g. push-deactivation
  // calls that capture the outgoing user's token before it's cleared from
  // localStorage -- 2026-08-15 Notifications red-team fix, CRITICAL).
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Advertise client platform so backend can apply platform-specific logic
  // (e.g. iOS EXIF exemption for proof timestamp — Spec 179c)
  config.headers['X-Client-Platform'] = detectPlatform();

  // Send selected organization ID so multi-org users hit the correct org
  const storedOrg = localStorage.getItem('selectedOrganization');
  if (storedOrg) {
    try {
      const org = JSON.parse(storedOrg);
      if (org.id) {
        config.headers['X-Organization-Id'] = String(org.id);
      }
    } catch {
      // Ignore malformed JSON — middleware falls back to LIMIT 1
    }
  }

  // Don't set Content-Type for FormData - let the browser handle it
  // This ensures the correct boundary is included for multipart/form-data
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  // Add CSRF token for state-changing requests
  const method = config.method?.toUpperCase();
  if (method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
    const csrf = await getCSRFToken();
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf;
    }
  }

  return config;
}

api.interceptors.request.use(async (config) => {
  try {
    return await attachRequestHeaders(config);
  } catch (err) {
    const marked = err instanceof Error ? err : new Error(String(err));
    throw Object.assign(marked, { noSlotHeld: true });
  }
});

// 0zzz-fe: local write lock while the org picker is open (defence in depth —
// see setOrgSelectionPending above). Registered last, so under axios's
// last-registered-first request ordering it runs FIRST: a blocked write is
// rejected before any slot is acquired; its error is marked noSlotHeld so no
// release handler gives back a slot it never took.
// A rejected request never reaches dispatchRequest, so no network call is
// made. GETs are never blocked — only an ambiguous/stale org makes a WRITE
// unsafe, not a read.
api.interceptors.request.use((config) => {
  // Runs first (registered last): stamp creation time for the post-pick grace window.
  (config as unknown as Record<string, unknown>)[REQUEST_CREATED_AT] = Date.now();
  const method = config.method?.toUpperCase();
  if (orgSelectionPending && method && WRITE_METHODS.has(method) && !isOrgSelectionExempt(config.url)) {
    // Re-announce to whatever's already handling this (the open picker just
    // ignores a second claim — see OrgPickerSheet's isOpenRef check) rather
    // than assume nothing is listening; this call only exists because the
    // picker IS open (nothing else sets orgSelectionPending true).
    window.dispatchEvent(
      new CustomEvent<OrgPickerRequiredEventDetail>(ORG_PICKER_REQUIRED_EVENT, {
        detail: { reason: 'selection' },
        cancelable: true,
      })
    );
    const blocked = Object.assign(new Error('Organization selection pending — write blocked client-side'), {
      isAxiosError: true,
      noSlotHeld: true,
      config,
      response: {
        status: 403,
        statusText: 'Forbidden',
        headers: {},
        config,
        data: { error: 'Choose which organization this is for.', code: 'ORG_SELECTION_PENDING' },
      },
    });
    return Promise.reject(blocked);
  }
  return config;
});

// Release concurrency slot on every response (success or error)
api.interceptors.response.use(
  (response) => { releaseSlot(); return response; },
  (error) => { releaseSlotUnlessNoneHeld(error); return Promise.reject(error); }
);


// Error handling response interceptor (auth, CSRF, session management)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Check if this is an auth error (401 or 403 with specific messages)
    if (error.response) {
      const { status, data } = error.response;
      const errorMessage = data?.error || '';
      const errorCode = data?.code || '';

      // CSRF token expired or invalid - refresh and retry once
      if (status === 403 && (
        errorCode === 'CSRF_TOKEN_INVALID' ||
        errorCode === 'CSRF_TOKEN_MISSING'
      )) {
        // Clear the stale token and retry the request once
        csrfToken = null;

        // Only retry once to prevent infinite loops
        if (!error.config._csrfRetried) {
          error.config._csrfRetried = true;
          try {
            const newToken = await fetchCSRFToken();
            if (newToken) {
              error.config.headers['X-CSRF-Token'] = newToken;
              return api.request(error.config);
            }
          } catch {
            // If CSRF refresh fails, continue with original error
          }
        }
      }

      // Spec 327: server-side ToS enforcement. Tell ToSReacceptanceModal to
      // re-check (it opens); not a session failure, so no redirect or logout.
      if (status === 403 && errorCode === 'TOS_ACCEPTANCE_REQUIRED') {
        window.dispatchEvent(new Event(TOS_REQUIRED_EVENT));
        return Promise.reject(error);
      }

      // 0zzz-fe (found in the staging Chrome pass): a stale-org refusal for a
      // request that was sent for an org the user has since switched AWAY from
      // is outdated news. After a pick, queries still mounted for the old org
      // refetch once and 403; acting on that cleared the NEWLY picked org and
      // hard-redirected, losing the form the picker had just saved. The request's
      // own X-Organization-Id says which org it was about.
      const isOrgRefusal =
        (status === 403 &&
          (errorCode === 'ORG_HEADER_INVALID' ||
            errorCode === 'ORG_SELECTION_REQUIRED' ||
            errorCode === 'ORG_MEMBERSHIP_STALE' ||
            errorMessage.includes('Not a member of this organization'))) ||
        // Same leftover class on the 404 path: a post-pick refetch for the org
        // just left must not clear the newly picked org (cross-provider review).
        (status === 404 && errorMessage.includes('Organization not found'));
      if (isOrgRefusal) {
        const refusedMethod = error.config?.method?.toUpperCase();
        if (
          withinPostPickGrace(error.config as Record<string, unknown> | undefined) &&
          !(refusedMethod && WRITE_METHODS.has(refusedMethod))
        ) {
          return Promise.reject(error);
        }
        const sentOrgId = error.config?.headers?.['X-Organization-Id'];
        let currentOrgId: string | undefined;
        try {
          currentOrgId = JSON.parse(localStorage.getItem('selectedOrganization') || 'null')?.id;
        } catch {
          currentOrgId = undefined;
        }
        if (sentOrgId && currentOrgId && String(sentOrgId) !== String(currentOrgId)) {
          return Promise.reject(error);
        }
        // Refetches that run AFTER the pick carry the new org's header but the
        // old org in the URL (e.g. GET /organizations/<old>/settings) — so also
        // match the orgs the user explicitly moved away from in the picker.
        const url = String(error.config?.url ?? '');
        for (const abandoned of abandonedOrgIds) {
          // An org the user has since re-selected is not a leftover: its
          // refusals are real and must reach recovery (cross-provider review).
          if (currentOrgId && abandoned === String(currentOrgId)) continue;
          if (String(sentOrgId) === abandoned || url.includes(abandoned)) {
            return Promise.reject(error);
          }
        }
      }

      // ORG_HEADER_INVALID (0zzz-ev): the backend refused a write because the
      // stored org is no longer one of the user's memberships.
      // ORG_SELECTION_REQUIRED (0zzz-fe): a header-less write from a
      // multi-org user under ORG_SELECTION_ENFORCEMENT=enforce — nothing was
      // stored to begin with.
      // 403 "Not a member of this organization" on a WRITE (0zzz-fe, found in
      // the staging Chrome pass): routes that take the org from the body or URL
      // instead of requireOrganization (task create, for one) refuse a stale
      // org with this message. Same situation, same recovery.
      //
      // 0zzz-fe: give OrgPickerSheet (if mounted) a chance to resolve this
      // in place instead of hard-redirecting and discarding in-progress
      // input. The event is CANCELABLE — a listener calls preventDefault()
      // to claim it, which stops window.dispatchEvent() from returning true.
      // When claimed, we must NOT clear localStorage or redirect: the stored
      // org (for ORG_HEADER_INVALID) stays in place on purpose while the
      // picker is open, so any background write during that window keeps
      // failing closed instead of falling back header-less to the user's
      // oldest org — the exact misroute 0zzz-ev prevents. If nothing claims
      // it (no picker mounted, e.g. an older client build), fall through to
      // exactly the pre-existing clear+redirect behavior.
      const requestMethod = error.config?.method?.toUpperCase();
      const isNotMember = status === 403 && errorMessage.includes('Not a member of this organization');
      const isNotMemberWriteAny = isNotMember && !!requestMethod && WRITE_METHODS.has(requestMethod);
      // "Not a member of this organization" is a GENERIC permission error
      // (requireOrgMembership also checks target users and other orgs — e.g. a
      // Dom scheduling travel for a sub who isn't in the selected org). It only
      // means "your selected org is stale" when the request's BODY names the
      // stored org (task create does). URL-named orgs don't count — see
      // orgIdInRequestBody. Anything else is an ordinary refusal: reject with no
      // picker, clear or redirect, and let the screen show its error
      // (cross-provider review, HIGH + fifth-pass MEDIUM).
      let storedOrgIdNow: string | undefined;
      try {
        storedOrgIdNow = JSON.parse(localStorage.getItem('selectedOrganization') || 'null')?.id;
      } catch {
        storedOrgIdNow = undefined;
      }
      const namedByRequest = isNotMemberWriteAny ? orgIdInRequestBody(error.config) : undefined;
      // ORG_MEMBERSHIP_STALE (0zzz-fe): the server says the CALLER failed the check on the org they
      // selected, so no body-naming guess is needed. The message heuristics stay for older servers
      // and for the sites that do not emit the code yet.
      const staleByCode = isNotMemberWriteAny && errorCode === 'ORG_MEMBERSHIP_STALE';
      const isNotMemberWrite =
        staleByCode ||
        (isNotMemberWriteAny && !!namedByRequest && !!storedOrgIdNow && namedByRequest === String(storedOrgIdNow));
      // A body naming a DIFFERENT org is an ordinary refusal (target org/user).
      // A write naming NO org falls through to the read-path recovery below —
      // master's behavior — so a genuinely stale selection still self-corrects.
      if (isNotMemberWriteAny && !isNotMemberWrite && namedByRequest) {
        return Promise.reject(error);
      }
      if (
        status === 403 &&
        (errorCode === 'ORG_HEADER_INVALID' || errorCode === 'ORG_SELECTION_REQUIRED' || isNotMemberWrite)
      ) {
        let staleOrgId: string | undefined;
        if (errorCode !== 'ORG_SELECTION_REQUIRED') {
          try {
            const stored = JSON.parse(localStorage.getItem('selectedOrganization') || 'null');
            staleOrgId = stored?.id;
          } catch {
            // malformed stored org — leave staleOrgId undefined, picker just won't exclude anything
          }
        }

        const notCancelled = window.dispatchEvent(
          new CustomEvent<OrgPickerRequiredEventDetail>(ORG_PICKER_REQUIRED_EVENT, {
            detail: {
              reason: errorCode === 'ORG_SELECTION_REQUIRED' ? 'selection' : 'stale',
              staleOrgId,
            },
            cancelable: true,
          })
        );

        if (!notCancelled) {
          // A listener (OrgPickerSheet) claimed it — it will handle
          // re-selection; don't clear or redirect out from under it.
          return Promise.reject(error);
        }

        localStorage.removeItem('selectedOrganization');
        if (!isOnboardingExemptPath(window.location.pathname)) {
          window.location.href = '/organizations';
        }
        return Promise.reject(error);
      }

      // Reads: user is not a member of the organization in localStorage (stale org data)
      if (isNotMember) {
        // 0zzz-fe: while the org picker is open it owns recovery — clearing the
        // stored org and reloading here would throw away the form it protects.
        if (orgSelectionPending) {
          return Promise.reject(error);
        }
        localStorage.removeItem('selectedOrganization');
        // isOnboardingExemptPath, not just /organizations — see the
        // NO_HIERARCHY block below for why a bare /organizations check isn't
        // enough (2026-07-29 safety review finding).
        if (!isOnboardingExemptPath(window.location.pathname)) {
          window.location.href = '/organizations';
        }
        return Promise.reject(error);
      }

      // Organization in localStorage no longer exists (deleted org)
      if (status === 404 && errorMessage.includes('Organization not found')) {
        // 0zzz-fe: an open picker owns recovery (a background 404 must not tear it down).
        if (orgSelectionPending) {
          return Promise.reject(error);
        }
        localStorage.removeItem('selectedOrganization');
        if (!isOnboardingExemptPath(window.location.pathname)) {
          window.location.href = '/organizations';
        }
        return Promise.reject(error);
      }

      // User removed from all organizations (stale org in localStorage) —
      // OR never completed onboarding's identity step in the first place.
      if (status === 404 && errorCode === 'NO_HIERARCHY') {
        // 0zzz-fe: an open picker owns recovery (its zero-orgs state covers this case).
        if (orgSelectionPending) {
          return Promise.reject(error);
        }
        // 0zzz-uhy: a safeword trigger by a user in no hierarchy (the lock-screen SafewordModal runs on ANY path) is
        // answered on screen by the caller (an honest dead end + crisis resources). A hard redirect here would wipe
        // that message and could cut off the platform alert that the caller sends next.
        if (/\/safeword\/(trigger|soft-limit|hard-limit)\b/.test(String(error.config?.url ?? ''))) {
          return Promise.reject(error);
        }
        localStorage.removeItem('selectedOrganization');
        // isOnboardingExemptPath (not just the two self-referential checks
        // this used to be) — a 2026-07-29 safety review found that a user on
        // /settings (or /billing, /support, /account, etc.) could still get
        // hard-redirected away by this interceptor, because PillarLayout
        // unconditionally mounts ActivePopBanner even on those pages, and
        // ActivePopBanner's own pop-notification call has no org guard of
        // its own. That defeated useRequireOnboarding's EXEMPT_PREFIXES
        // promise ("a user must always be able to reach these regardless of
        // onboarding state") at the one mechanism that actually decides
        // where a user lands in practice. Same shared list, single source
        // of truth — see utils/onboardingExemptPaths.ts.
        if (!isOnboardingExemptPath(window.location.pathname)) {
          // This hard redirect (a real browser navigation) always wins the
          // race against useRequireOnboarding's soft React navigate() —
          // confirmed via live staging walkthrough, 2026-07-28: a user who
          // abandons onboarding and then navigates anywhere consistently
          // lands here via THIS interceptor, not useRequireOnboarding's own
          // redirect. So THIS is the actual decision point for where a
          // never-onboarded user ends up, not the hook — route to /register
          // (Spec 289: onboarding collection lives in its step machine now,
          // /onboard is retired) instead of the generic /organizations
          // create-org flow, which skips the identity/role questions
          // entirely. A user who completed onboarding once and later left
          // their only org still goes to /organizations, same as before.
          let destination = '/organizations';
          try {
            const cachedUser = JSON.parse(localStorage.getItem('user') || 'null');
            if (cachedUser && !cachedUser.onboardingIdentityCompletedAt) {
              destination = '/register';
            }
          } catch {
            // malformed cached user — fall through to /organizations
          }

          if (destination === '/organizations') {
            // This is a silent hard redirect with no explanation shown to
            // the user — flagged 2026-07-28 as the likely real cause of a
            // "can't navigate" report (bug-registry.md): a background call
            // (e.g. the pop-notification banner) can 404 with this code on
            // ANY page, so an org-less user can get bounced here with zero
            // context. The flag lets OrganizationsPage show a one-time
            // explanation. Not set for the /register destination — that
            // banner only lives on OrganizationsPage, and a stale flag would
            // otherwise sit in sessionStorage until an unrelated later visit
            // to /organizations shows an inaccurate "brought you here"
            // explanation.
            sessionStorage.setItem('orgRedirectReason', 'no_hierarchy');
          }
          window.location.href = destination;
        }
        return Promise.reject(error);
      }

      // Access token expired — attempt silent refresh before giving up.
      // Uses a singleton promise so concurrent 401s share one refresh call
      // (refresh tokens are one-time-use; racing would consume & fail).
      if (status === 401 && errorCode === 'TOKEN_EXPIRED' && !error.config._refreshRetried) {
        error.config._refreshRetried = true;

        if (!refreshTokenPromise) {
          const storedRefresh = localStorage.getItem('refreshToken');
          if (storedRefresh) {
            refreshTokenPromise = axios.post(`${API_URL}/auth/refresh`, { refreshToken: storedRefresh })
              .then((resp) => {
                const { token: newAccess, refreshToken: newRefresh } = resp.data;
                localStorage.setItem('authToken', newAccess);
                localStorage.setItem('refreshToken', newRefresh);
                return { token: newAccess, refreshToken: newRefresh };
              })
              .catch((err) => {
                if (err?.code === 'ERR_NETWORK' || err?.code === 'ECONNABORTED') {
                  console.warn('[auth] Refresh failed — network error, not invalidating session');
                }
                // intentional: null return causes caller to fall through to session-expired handler
                return null;
              })
              .finally(() => { refreshTokenPromise = null; });
          }
        }

        if (refreshTokenPromise) {
          try {
            const result = await refreshTokenPromise;
            if (result) {
              error.config.headers.Authorization = `Bearer ${result.token}`;
              return api.request(error.config);
            }
          } catch {
            // Refresh failed — fall through to session expired handling
          }
        }
      }

      // Server temporarily unavailable (DB pool exhausted) — retry once after brief delay
      if (status === 503 && errorCode === 'DB_UNAVAILABLE' && !error.config._503Retried) {
        error.config._503Retried = true;
        await new Promise(r => setTimeout(r, 1000));
        return api.request(error.config);
      }

      // Session expired or token invalidated
      if (status === 401 ||
          (status === 403 && (
            errorMessage.includes('Token has been invalidated') ||
            errorMessage.includes('Invalid or expired token') ||
            errorMessage.includes('User not found')
          ))
      ) {
        // Don't trigger for login/register/refresh endpoints (those have their own error handling)
        const isAuthEndpoint = error.config?.url?.includes('/auth/login') ||
                               error.config?.url?.includes('/auth/register') ||
                               error.config?.url?.includes('/auth/refresh');

        if (!isAuthEndpoint) {
          const currentToken = localStorage.getItem('authToken');
          const requestAuthHeader = error.config?.headers?.Authorization as string | undefined;
          const requestToken = requestAuthHeader ? requestAuthHeader.replace(/^Bearer\s+/i, '') : null;

          // Cross-tab (or cross-trigger, e.g. this tab's own proactive-refresh
          // interval racing the reactive 401 path above) staleness: another
          // caller already refreshed and wrote a fresh token to localStorage
          // while this request was failing — either in flight against the old
          // token, or its own reactive-refresh attempt above just consumed an
          // already-spent one-time-use refresh token from another tab's
          // refresh call. Proactive refresh (module load / 5-min interval /
          // visibility-change / mobile foreground) makes this race far more
          // likely than it used to be under the old reactive-only trigger, so
          // it's no longer safe to assume "still 401 = truly expired" without
          // checking. Retry once against the CURRENT token before declaring
          // the session expired — declaring it expired here would wrongly log
          // out a tab that actually still has a perfectly valid session.
          if (currentToken && requestToken && currentToken !== requestToken && !error.config._crossTabRetried) {
            error.config._crossTabRetried = true;
            error.config.headers.Authorization = `Bearer ${currentToken}`;
            return api.request(error.config);
          }

          if (currentToken) {
            // Dispatch event for AuthContext to handle
            dispatchAuthError('Your session has expired. Please log in again.');
          }
        }
      }

      // Account suspended
      if (status === 403 && errorMessage.includes('suspended')) {
        dispatchAuthError('Your account has been suspended. Please contact support.');
      }
    }

    return Promise.reject(error);
  }
);

export default api;
